/**
 * React rendering layer for the manufacturing cell-formatting presets.
 *
 * Each `formatX` helper returns a `(value, row) => ReactNode` suitable for
 * `DataTableColumn.cell` / `VirtualTableColumn.cell`, so a module declares a KPI
 * column once and gets consistent rounding, units and threshold coloring:
 *
 * ```tsx
 * import { DataTable, formatPercentage, formatState } from '@tyndo/ui-kit';
 *
 * const columns: DataTableColumn<LineKpi>[] = [
 *   { id: 'fpy', header: 'FPY', accessor: 'firstPassYield', align: 'right',
 *     cell: formatPercentage({ goodAtOrAbove: 99, warnAtOrAbove: 97 }) },
 *   { id: 'state', header: 'State', accessor: 'state', cell: formatState() },
 * ];
 * ```
 *
 * The pure threshold/text decisions live in `table-formatters.ts` (unit-tested
 * in the node sweep); this file only maps the resolved `tone` onto design-token
 * classes and renders.
 */

import * as React from 'react';
import { Badge } from '../primitives/badge';
import { cn } from '../lib/utils';
import {
  resolvePercentage,
  resolveDuration,
  resolveCurrency,
  resolveNumeric,
  resolveState,
  resolveConfidence,
  type CellTone,
  type FormattedCell,
  type PercentageFormatOptions,
  type DurationFormatOptions,
  type CurrencyFormatOptions,
  type NumericFormatOptions,
  type ConfidenceFormatOptions,
} from './table-formatters.ts';

export type {
  CellTone,
  FormattedCell,
  PercentageFormatOptions,
  DurationFormatOptions,
  CurrencyFormatOptions,
  NumericFormatOptions,
  ConfidenceFormatOptions,
} from './table-formatters.ts';

export {
  resolvePercentage,
  resolveDuration,
  resolveCurrency,
  resolveNumeric,
  resolveState,
  resolveConfidence,
  humanizeDuration,
} from './table-formatters.ts';

/** Tone → text color class (for inline KPI text like a yield %). */
const TONE_TEXT_CLASS: Record<CellTone, string> = {
  neutral: 'text-foreground',
  good: 'text-[hsl(var(--state-running))]',
  warning: 'text-[hsl(var(--alarm-minor))]',
  critical: 'text-[hsl(var(--alarm-critical))]',
  info: 'text-brand-primary',
};

/** Tone → Badge variant (for state badges). */
const TONE_BADGE_VARIANT: Record<
  CellTone,
  'success' | 'warning' | 'critical' | 'info' | 'muted'
> = {
  neutral: 'muted',
  good: 'success',
  warning: 'warning',
  critical: 'critical',
  info: 'info',
};

/** Tone → confidence-gradient background (subtle fill behind the % text). */
const TONE_GRADIENT_CLASS: Record<CellTone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  good: 'bg-[hsl(var(--state-running)/0.15)] text-[hsl(var(--state-running))]',
  warning: 'bg-[hsl(var(--alarm-minor)/0.15)] text-[hsl(var(--alarm-minor))]',
  critical: 'bg-[hsl(var(--alarm-critical)/0.15)] text-[hsl(var(--alarm-critical))]',
  info: 'bg-brand-primary/15 text-brand-primary',
};

/** Render a resolved cell as threshold-colored inline text. */
function TonedText({ cell }: { cell: FormattedCell }): React.ReactElement {
  return (
    <span
      className={cn('tabular-nums', TONE_TEXT_CLASS[cell.tone])}
      title={cell.title}
    >
      {cell.text}
    </span>
  );
}

type CellRenderer = (value: unknown) => React.ReactNode;

/**
 * Percentage with threshold coloring (FPY, OEE, yield, utilization). Returns a
 * `DataTableColumn.cell` renderer; pass per-KPI bounds.
 */
export function formatPercentage(opts?: PercentageFormatOptions): CellRenderer {
  return (value) => <TonedText cell={resolvePercentage(value, opts)} />;
}

/** Humanized duration (cycle time, downtime) with optional SLA coloring. */
export function formatDuration(opts?: DurationFormatOptions): CellRenderer {
  return (value) => <TonedText cell={resolveDuration(value, opts)} />;
}

/** Locale-formatted currency (cost, variance, journal amounts). */
export function formatCurrency(opts?: CurrencyFormatOptions): CellRenderer {
  return (value) => <TonedText cell={resolveCurrency(value, opts)} />;
}

/** Fixed-precision number with optional unit + threshold coloring. */
export function formatNumeric(opts?: NumericFormatOptions): CellRenderer {
  return (value) => <TonedText cell={resolveNumeric(value, opts)} />;
}

/**
 * Lifecycle / disposition state as a toned `Badge` (ISA-88 / ISA-95 states,
 * pass/fail dispositions). Pass `toneOverrides` for module-specific states.
 */
export function formatState(toneOverrides?: Record<string, CellTone>): CellRenderer {
  return (value) => {
    const cell = resolveState(value, toneOverrides);
    if (cell.text === '—') return <span className="text-muted-foreground">—</span>;
    return (
      <Badge variant={TONE_BADGE_VARIANT[cell.tone]} className="gap-1.5">
        {cell.text}
      </Badge>
    );
  };
}

/**
 * Model-confidence score as a gradient-filled pill — low confidence reads red
 * so the operator knows the AI suggestion needs review.
 */
export function formatConfidence(opts?: ConfidenceFormatOptions): CellRenderer {
  return (value) => {
    const cell = resolveConfidence(value, opts);
    if (cell.text === '—') return <span className="text-muted-foreground">—</span>;
    return (
      <span
        className={cn(
          'inline-flex min-w-[3rem] justify-center rounded px-1.5 py-0.5 text-xs font-medium tabular-nums',
          TONE_GRADIENT_CLASS[cell.tone]
        )}
        title={cell.title}
      >
        {cell.text}
      </span>
    );
  };
}
